import React, { useState } from "react";
import PropTypes from "prop-types";
import { Badge } from "react-bootstrap";
import SectionCard from "../SectionCard";
import SectionSkeleton from "../SectionSkeleton";

// Qualitative on purpose: no measured lift number backs a percentage, so this is a
// fixed string, not a template with a stat plugged in. If a defensible measured value ever shows
// up, that's a one-string change here — not a reason to invent one now.
const ENCOURAGEMENT_COPY = "Users with a completed profile are more likely to get coverage!";

const DEFAULT_TITLE = "Complete Your Profile";

// Design draws the score out of 10 (both the expert and PR-profile completion views already score
// on that scale), so this is real data, not a placeholder.
const MAX_SCORE = 10;

export function isChecklistComplete(score) {
  return Boolean(score) && Number(score.completion_score) >= MAX_SCORE;
}

/**
 * ChecklistCard — chrome half of the profile's "Complete Your Profile" rail card (the data half is
 * use_profile_completion). Owner/admin-only content: title, a green completion-score badge, and a
 * collapsible body — a fixed qualitative encouragement line, one row per completion factor
 * (checkmark vs. empty circle), and, once every factor is done, a "Hide Checklist" button.
 *
 * Only a complete (10/10) checklist can be collapsed or hidden. Figma: "Once the checklist is
 * completed, the user can hide the checklist from showing up". An incomplete one is always open,
 * with no chevron or Hide button. A complete one starts collapsed to its header (title + badge +
 * chevron) on every visit, and the chevron opens and closes it. Hide Checklist calls `onHide`: the
 * caller drops the card (useProfileCompletion's `hide`) until the checklist is incomplete again.
 *
 * Model-agnostic like TeamMembersCard/MediaOutletsRailCard: it knows nothing about experts vs PR
 * profiles, only the `factors` list the caller hands it — PR's 6-factor view and Expert's 7-factor
 * view share this one component rather than two near-copies. It does not gate on viewer role either;
 * that's the caller's job (same `viewerContext?.editable` pattern every other owner-only card uses).
 *
 * Driven by hook state and self-collapsing only while empty: a skeleton while the score loads,
 * nothing at all once it settles empty (never-throws contract). Hidden below sm (skeleton branch
 * too): at phone widths the checklist eats most of the stacked column without earning it.
 *
 * @param {Object} props
 * @param {Object|null} props.score - The resolved completion-scores view row (`completion_score`
 *   plus the `has_*` boolean factor columns), or `null` while loading/idle/errored
 * @param {Array<{ key: string, label: string }>} props.factors - Which boolean columns on `score`
 *   to render as which labels, in display order
 * @param {boolean} [props.isLoading] - True while the score read is in flight
 * @param {Object.<string, Function>} [props.factorActions] - Optional factor key -> callback map.
 *   A row whose key appears here renders as a clickable button (deep-linking to that factor's edit
 *   modal, typically `() => setOpenEditModal(SECTION_KEY)`); every other row stays static, exactly as
 *   before this prop existed. Omitting the prop entirely reproduces the original static markup. A
 *   row for an already-completed factor never renders as a button, regardless of factorActions --
 *   there is nothing left to deep-link an owner to.
 * @param {string} [props.title] - Card heading. Defaults to the PR/Expert copy; the company variant
 *   overrides it (entity pages aren't "users").
 * @param {boolean} [props.showEncouragement] - Whether to render the fixed encouragement line below
 *   the heading. Off for the company variant, on everywhere else.
 * @param {Function} [props.onHide] - Hide Checklist's handler
 */
function ChecklistCard({
  score,
  factors,
  isLoading = false,
  factorActions = {},
  title = DEFAULT_TITLE,
  showEncouragement = true,
  onHide,
}) {
  const [expanded, setExpanded] = useState(false);

  if (isLoading)
    return (
      <div className="d-none d-sm-block">
        <SectionSkeleton lines={factors.length || 3} />
      </div>
    );
  if (!score) return null;

  const complete = isChecklistComplete(score);
  const collapsed = complete && !expanded;

  return (
    <SectionCard
      className="d-none d-sm-flex"
      id="profile-checklist"
      title={title}
      headerActions={
        <>
          {complete ? (
            // Figma's completed-state badge: a white pill with a green stroke and dot.
            <span className="checklist-done-badge badge rounded-pill d-inline-flex align-items-center gap-1 bg-white">
              <span className="visually-hidden">Profile 100% complete. </span>🎉 Great Job!
            </span>
          ) : (
            <Badge bg="success" pill>
              {score.completion_score}/{MAX_SCORE}
            </Badge>
          )}
          {complete && (
            <button
              type="button"
              className="btn btn-link p-0 ms-2 text-muted"
              aria-expanded={!collapsed}
              aria-label={collapsed ? "Expand checklist" : "Collapse checklist"}
              onClick={() => setExpanded(collapsed)}
            >
              <i className={collapsed ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-up"} />
            </button>
          )}
        </>
      }
    >
      {!collapsed && (
        <>
          {showEncouragement && <p className="text-muted mb-3">{ENCOURAGEMENT_COPY}</p>}
          <ul className="list-unstyled mb-3">
            {factors.map((factor) => {
              const factorComplete = Boolean(score[factor.key]);
              const onEdit = factorActions[factor.key];
              const actionable = Boolean(onEdit) && !factorComplete;
              // The label's color is the affordance: link-colored means "click me to finish this
              // step", muted means "nothing to do here" (done, or no deep-link wired). The muted
              // class must stay OFF the actionable label or it overrides btn-link's link color.
              const rowContent = (
                <>
                  <i
                    className={
                      factorComplete
                        ? "fa-solid fa-circle-check text-success me-2"
                        : "fa-regular fa-circle text-muted me-2"
                    }
                  />
                  <span className={actionable ? "" : "text-muted"}>{factor.label}</span>
                </>
              );
              return (
                <li key={factor.key} className="d-flex align-items-center mb-2">
                  {actionable ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0 d-flex align-items-center text-start"
                      // Neutralize .btn's own font sizing and flex gap (and border-0 above kills
                      // its 1px transparent border) so this row's icon and label sit exactly
                      // where the plain rows' do — me-2 on the icon is the only spacing.
                      style={{ font: "inherit", gap: 0 }}
                      onClick={onEdit}
                    >
                      {rowContent}
                    </button>
                  ) : (
                    rowContent
                  )}
                </li>
              );
            })}
          </ul>
          {complete && (
            <button
              type="button"
              className="checklist-hide-btn btn btn-outline-secondary btn-sm w-100"
              onClick={onHide}
            >
              Hide Checklist
            </button>
          )}
        </>
      )}
    </SectionCard>
  );
}

ChecklistCard.propTypes = {
  score: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string])),
  factors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
  factorActions: PropTypes.objectOf(PropTypes.func),
  title: PropTypes.string,
  showEncouragement: PropTypes.bool,
  onHide: PropTypes.func,
};

export default ChecklistCard;
